import { useEffect, useRef } from 'react';

/**
 * Hook to attach Google Places Autocomplete to an input field
 * IMPORTANT: Ensures normal typing is never blocked
 * 
 * @param {Function} onPlaceSelected - Callback when user selects an address
 * @returns {Object} - { inputRef } to attach to the input element
 */
export const useGooglePlacesAutocomplete = (onPlaceSelected) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const callbackRef = useRef(onPlaceSelected);

  console.log('🎯 useGooglePlacesAutocomplete hook called, creating refs');
  
  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    console.log('🎯 useGooglePlacesAutocomplete useEffect running');
    const initAutocomplete = () => {
      // Check if input ref is available
      if (!inputRef.current) {
        console.log('⚠️ Input ref not ready for Google Places');
        return false;
      }
      
      // Check if Google Places API is loaded
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.log('⚠️ Google Maps/Places not loaded yet');
        return false;
      }
      
      // CRITICAL: Check if input is actually in the DOM (important for portal-rendered components)
      if (!document.body.contains(inputRef.current)) {
        console.log('⚠️ Input ref exists but not yet in DOM (portal rendering?)');
        return false;
      }

      // Don't re-initialize if already exists
      if (autocompleteRef.current) {
        console.log('✅ Google Places autocomplete already initialized');
        return true;
      }

      try {
        console.log('🔄 Initializing Google Places autocomplete...');
        const input = inputRef.current;
        
        // CRITICAL: Ensure input is always editable
        input.removeAttribute('readonly');
        input.removeAttribute('disabled');
        input.autocomplete = 'off'; // Disable browser autocomplete to avoid conflicts
        
        // Create wrapper div for autocomplete if it doesn't exist
        const wrapper = document.createElement('div');
        wrapper.style.position = 'relative';
        wrapper.style.display = 'inline-block';
        wrapper.style.width = '100%';
        
        // Only wrap if not already wrapped
        if (input.parentNode.tagName !== 'DIV' || !input.parentNode.style.position) {
          const parent = input.parentNode;
          parent.insertBefore(wrapper, input);
          wrapper.appendChild(input);
        }
        
        // Create autocomplete instance
        const autocomplete = new window.google.maps.places.Autocomplete(input, {
          types: ['address'],
          componentRestrictions: { country: 'us' },
          fields: ['address_components', 'formatted_address']
        });
        
        autocompleteRef.current = autocomplete;
        
        console.log('✅ Google Places autocomplete initialized successfully');
        console.log('📍 Autocomplete instance:', autocomplete);
        console.log('📍 Input element:', input);
        
        // Debug: Log when user types in the input
        const handleInputDebug = (e) => {
          console.log('⌨️ User typing in autocomplete input:', e.target.value);
          const pacContainer = document.querySelector('.pac-container');
          console.log('⌨️ pac-container in DOM:', pacContainer);
          if (pacContainer) {
            console.log('⌨️ pac-container computed style:', {
              display: window.getComputedStyle(pacContainer).display,
              visibility: window.getComputedStyle(pacContainer).visibility,
              zIndex: window.getComputedStyle(pacContainer).zIndex,
              position: window.getComputedStyle(pacContainer).position,
              top: window.getComputedStyle(pacContainer).top,
              left: window.getComputedStyle(pacContainer).left
            });
          }
        };
        input.addEventListener('input', handleInputDebug);
        
        // Store the handler for cleanup
        autocomplete._inputDebugHandler = handleInputDebug;

        // CRITICAL: Ensure input stays editable and events work
        const ensureInputWorks = () => {
          input.removeAttribute('readonly');
          input.removeAttribute('disabled');
          input.style.pointerEvents = 'auto';
          input.style.userSelect = 'text';
        };
        
        // Run immediately and on intervals
        ensureInputWorks();
        const interval = setInterval(ensureInputWorks, 100);

        // Listen for place selection ONLY
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          
          // Only process if user actually selected something from dropdown
          if (!place || !place.address_components) {
            // User just pressed Enter or typed - don't interfere
            ensureInputWorks();
            return;
          }

          // Parse address components
          const addressData = {
            street1: '',
            street2: '',
            city: '',
            state: '',
            zip: '',
            country: 'US',
            formatted_address: place.formatted_address || ''
          };

          place.address_components.forEach((component) => {
            const types = component.types;
            
            if (types.includes('street_number')) {
              addressData.street1 = component.long_name;
            }
            if (types.includes('route')) {
              addressData.street1 += (addressData.street1 ? ' ' : '') + component.long_name;
            }
            if (types.includes('subpremise')) {
              addressData.street2 = component.long_name;
            }
            if (types.includes('locality')) {
              addressData.city = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
              addressData.state = component.short_name;
            }
            if (types.includes('postal_code')) {
              addressData.zip = component.long_name;
            }
            if (types.includes('postal_code_suffix')) {
              addressData.zip += '-' + component.long_name;
            }
            if (types.includes('country')) {
              addressData.country = component.short_name;
            }
          });

          // Debug: Log what we extracted
          console.log('🔍 Hook extracted address data:', addressData);
          console.log('🔍 Raw Google place object:', place);
          console.log('🔍 Input current value BEFORE modification:', inputRef.current?.value);

          // CRITICAL FIX: Google Places automatically fills input with formatted_address
          // which includes the full address (street, city, state, zip all in one line)
          // We need to IMMEDIATELY replace this with ONLY the street address
          // This must happen BEFORE calling onPlaceSelected so the controlled input doesn't override
          if (inputRef.current) {
            // Store the full formatted address Google added
            const googleFilledValue = inputRef.current.value;
            console.log('🔍 Google auto-filled input with:', googleFilledValue);
            
            // CRITICAL: Disable autocomplete temporarily to prevent it from showing suggestions
            // when we dispatch the input event
            const originalAutocomplete = inputRef.current.autocomplete;
            inputRef.current.setAttribute('autocomplete', 'disabled');
            inputRef.current.blur(); // Also blur to ensure dropdown doesn't show
            
            // Replace with just the street address
            inputRef.current.value = addressData.street1;
            console.log('✂️ Replaced input value with street address only:', addressData.street1);
            
            // Create and dispatch input event so React knows the value changed
            // This is crucial for controlled inputs
            const inputEvent = new Event('input', { bubbles: true });
            Object.defineProperty(inputEvent, 'target', {
              writable: false,
              value: {
                name: inputRef.current.name || 'address_line1',
                value: addressData.street1
              }
            });
            inputRef.current.dispatchEvent(inputEvent);
            console.log('✅ Dispatched input event with street address only');
            
            // Restore autocomplete attribute
            setTimeout(() => {
              if (inputRef.current) {
                inputRef.current.setAttribute('autocomplete', originalAutocomplete || 'off');
              }
            }, 200);
          }

          // Call the callback with parsed address AFTER fixing the input
          // Pass the addressData which has all fields properly separated
          console.log('📞 Calling onPlaceSelected with data:', {
            street1: addressData.street1,
            street2: addressData.street2,
            city: addressData.city,
            state: addressData.state,
            zip: addressData.zip
          });
          
          // Use callbackRef.current to always get the latest callback
          if (callbackRef.current) {
            callbackRef.current(addressData);
            console.log('✅ onPlaceSelected callback completed');
          } else {
            console.warn('⚠️ No onPlaceSelected callback provided!');
          }
          
          // Close the dropdown after selection - MutationObserver approach
          // This watches for when Google adds pac-container back and immediately hides it
          const observer = new MutationObserver(() => {
            const pacContainers = document.querySelectorAll('.pac-container');
            pacContainers.forEach(container => {
              if (!container.classList.contains('hide-after-selection')) {
                container.classList.add('hide-after-selection');
                console.log('👁️ Added hide-after-selection class to pac-container');
              }
            });
          });
          
          // Start observing the body for pac-container additions
          observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
          });
          
          // Hide immediately
          const pacContainers = document.querySelectorAll('.pac-container');
          pacContainers.forEach(container => {
            container.classList.add('hide-after-selection');
          });
          console.log('👁️ Applied hide-after-selection class immediately');
          
          // Stop observing after 3 seconds
          setTimeout(() => {
            observer.disconnect();
            console.log('👁️ Stopped observing pac-container');
          }, 3000);
          
          // Ensure input stays editable after selection
          ensureInputWorks();
        });

        // Store interval ref for cleanup
        autocomplete._ensureInputInterval = interval;

      } catch (error) {
        console.error('❌ Error initializing Google Places Autocomplete:', error);
        return false;
      }
      
      return true;
    };

    // Retry mechanism with timeout
    let retryCount = 0;
    const maxRetries = 40; // Try for 20 seconds (40 * 500ms) - increased for portal components
    
    const tryInit = () => {
      const success = initAutocomplete();
      
      if (!success && retryCount < maxRetries) {
        retryCount++;
        console.log(`🔄 Retry ${retryCount}/${maxRetries} - Waiting for Google Places or input DOM...`);
        setTimeout(tryInit, 500);
      } else if (!success) {
        console.error('❌ Failed to initialize Google Places after maximum retries');
        console.error('   Check: 1) Is Google Maps script loaded? 2) Is input in DOM? 3) Is component using portal?');
      } else {
        console.log('✅ Google Places Autocomplete fully initialized and ready!');
      }
    };

    // Check if Google Places is already loaded
    if (window.googlePlacesLoaded || (window.google && window.google.maps && window.google.maps.places)) {
      console.log('✅ Google Places already loaded, initializing...');
      // Increased initial delay for portal-rendered components (modals)
      setTimeout(tryInit, 500); // Increased from 250ms to 500ms
    } else {
      console.log('⏳ Waiting for Google Places to load...');
      // Wait for Google Places to load
      const handler = () => {
        console.log('✅ Google Places loaded event received');
        setTimeout(tryInit, 500); // Increased from 250ms to 500ms
      };
      window.addEventListener('google-places-loaded', handler);
      
      // Start retry mechanism with longer delay for portal-rendered components
      setTimeout(tryInit, 1500); // Increased from 1000ms to 1500ms
    }

    return () => {
      // Cleanup
      if (autocompleteRef.current) {
        if (autocompleteRef.current._ensureInputInterval) {
          clearInterval(autocompleteRef.current._ensureInputInterval);
        }
        // Remove debug event listener
        if (autocompleteRef.current._inputDebugHandler && inputRef.current) {
          inputRef.current.removeEventListener('input', autocompleteRef.current._inputDebugHandler);
        }
        if (window.google) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }
      }
      autocompleteRef.current = null;
    };
  }, []); // Empty deps - initialize ONCE, onPlaceSelected captured in closure

  return { inputRef };
};
